import type { GhanttTimelineProps, GhanttTimelineScale } from './types';
import { ghanttBuildTimelineSlots, ghanttSlotsTimeExtent, ghanttParseTime, type GhanttTimelineSlot } from './ganttTimeline';
import { ghanttDefaultColumnWidthForScale } from './ghanttModifierWheelZoom';
import { ghanttDebugLog } from './ghanttDebugLog';

export interface GhanttWheelZoomAnchor {
  anchorMs: number;
  anchorContentPx: number;
  /** Fracción 0..1 sobre el ancho de contenido de la rejilla (no lineal viewStart–viewEnd). */
  anchorFraction: number;
  anchorSource: 'grid' | 'linear';
}

/**
 * Instant bajo el puntero y fracción de ancla coherentes con la rejilla de slots actual.
 * Evita `anchorFraction: 1` cuando el scroll está al inicio pero `logicalContentWidth` está desfasado.
 */
export function ghanttResolveWheelZoomAnchor(
  timeline: GhanttTimelineProps,
  scrollLeft: number,
  xInViewport: number
): GhanttWheelZoomAnchor {
  const sc = (timeline.scale ?? 'day') as GhanttTimelineScale;
  const vs = ghanttParseTime(timeline.viewStart);
  const ve = ghanttParseTime(timeline.viewEnd);
  const slotW = timeline.columnWidthPx ?? ghanttDefaultColumnWidthForScale(sc);
  const slots = ghanttBuildTimelineSlots(timeline.viewStart, timeline.viewEnd, sc, {
    includeLeadingFullMonth: sc === 'day' ? timeline.includeLeadingFullMonth !== false : undefined,
  });
  const cell = snapCellPx(slotW);
  const maxContentPx = Math.max(0, slots.length * cell);
  const anchorContentPx = Math.max(0, Math.min(maxContentPx, scrollLeft + xInViewport));
  const anchorFraction = maxContentPx > 0 ? anchorContentPx / maxContentPx : 0.5;

  const fromGrid = ghanttContentPixelToAnchorMs(slots, sc, slotW, anchorContentPx);
  if (fromGrid !== null && Number.isFinite(fromGrid)) {
    return { anchorMs: fromGrid, anchorContentPx, anchorFraction, anchorSource: 'grid' };
  }

  const linearMs =
    Number.isFinite(vs) && Number.isFinite(ve) && ve > vs
      ? vs + anchorFraction * (ve - vs)
      : (vs + ve) / 2;
  return { anchorMs: linearMs, anchorContentPx, anchorFraction, anchorSource: 'linear' };
}

/**
 * Debe coincidir con {@link ghanttSnapSlotWidthPx} en `GhanttSlotStrip` (rejilla y scroll comparten ancho de celda).
 */
function snapCellPx(slotWidthPx: number): number {
  return Math.max(2, Math.round(slotWidthPx));
}

function slotEndExclusiveMs(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
  index: number
): number {
  if (index + 1 < slots.length) {
    return slots[index + 1]!.startMs;
  }
  const ext = ghanttSlotsTimeExtent(slots, scale);
  return ext?.endExclusiveMs ?? slots[index]!.startMs;
}

function findSlotIndexAtOrBefore(slots: readonly GhanttTimelineSlot[], anchorMs: number): number {
  if (slots.length === 0) return 0;
  if (anchorMs < slots[0]!.startMs) {
    return 0;
  }
  let lo = 0;
  let hi = slots.length - 1;
  let ans = hi;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (slots[mid]!.startMs <= anchorMs) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/**
 * Posición horizontal en px sobre el contenido scrollable (origen = inicio del primer slot),
 * alineada a la misma rejilla que `GhanttSlotStrip` / cabecera.
 */
export function ghanttContentPixelToAnchorMs(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
  slotWidthPx: number,
  contentPixel: number
): number | null {
  const cell = snapCellPx(slotWidthPx);
  if (slots.length === 0 || cell <= 0) return null;
  const totalPx = slots.length * cell;
  const ext = ghanttSlotsTimeExtent(slots, scale);
  /** Evita `floor(slotFloat) === slots.length` cuando el pixel cae en el borde derecho del contenido. */
  const p = Math.max(0, Math.min(contentPixel, Math.max(0, totalPx - 1e-3)));
  const slotFloat = p / cell;
  const i = Math.min(slots.length - 1, Math.max(0, Math.floor(slotFloat)));
  const u = slotFloat - i;
  const s0 = slots[i]!.startMs;
  const s1 = slotEndExclusiveMs(slots, scale, i);
  const dur = Math.max(1, s1 - s0);
  const clampedU = Math.max(0, Math.min(1, u));
  let ms = s0 + clampedU * dur;
  if (ext && ms >= ext.endExclusiveMs) {
    ms = ext.endExclusiveMs - 1;
  }
  ghanttDebugLog('anchor', 'ghanttContentPixelToAnchorMs', {
    contentPixel,
    p,
    slotFloat,
    slotIndex: i,
    anchorMs: ms,
    scale,
    cell,
    slotCount: slots.length,
  });
  return ms;
}

export function ghanttAnchorMsToContentPixel(
  slots: readonly GhanttTimelineSlot[],
  scale: GhanttTimelineScale,
  slotWidthPx: number,
  anchorMs: number
): number | null {
  const cell = snapCellPx(slotWidthPx);
  if (slots.length === 0 || cell <= 0) return null;
  const ext = ghanttSlotsTimeExtent(slots, scale);
  if (ext && anchorMs <= ext.startMs) {
    ghanttDebugLog('anchor', 'ghanttAnchorMsToContentPixel', {
      anchorMs,
      contentPx: 0,
      branch: 'at-or-before-start',
      scale,
      cell,
      slotCount: slots.length,
    });
    return 0;
  }
  if (ext && anchorMs >= ext.endExclusiveMs) {
    const endPx = slots.length * cell;
    ghanttDebugLog('anchor', 'ghanttAnchorMsToContentPixel', {
      anchorMs,
      contentPx: endPx,
      branch: 'at-or-after-end',
      scale,
      cell,
      slotCount: slots.length,
    });
    return endPx;
  }
  const i = findSlotIndexAtOrBefore(slots, anchorMs);
  const s0 = slots[i]!.startMs;
  const s1 = slotEndExclusiveMs(slots, scale, i);
  const dur = Math.max(1, s1 - s0);
  const u = Math.max(0, Math.min(1, (anchorMs - s0) / dur));
  const contentPx = (i + u) * cell;
  ghanttDebugLog('anchor', 'ghanttAnchorMsToContentPixel', {
    anchorMs,
    slotIndex: i,
    u,
    contentPx,
    scale,
    cell,
    slotCount: slots.length,
    atStart: ext ? anchorMs <= ext.startMs : false,
    atEnd: ext ? anchorMs >= ext.endExclusiveMs : false,
  });
  return contentPx;
}
